<template>
  <div class="download-page custom-scrollbar">
    <!-- 呼吸灯背景 -->
    <div class="breathing-background"></div>
    <div class="breathing-overlay"></div>
    
    <!-- 雪花容器 -->
    <div class="snowflakes" aria-hidden="true">
      <div class="snowflake" v-for="i in 20" :key="i">❅</div>
    </div>

    <div v-if="isloading" class="fullscreen-loader">
      <fulfilling-bouncing-circle-spinner :animation-duration="1000" :size="60" color="#ff1d5e" />
    </div>

    <div class="content-wrapper" v-show="!isloading">
      <header class="header">
        <div class="logo-container">
          <img class="logo" src="/logo.png" alt="">
        </div>
        <h1 class="title">YW LOG ZAP</h1>
        <p class="subtitle">YW日志飞刃 · 开发者专属工具</p>
      </header>

      <div class="main-content">
        <div class="intro-section">
          <h2 class="section-title">✨ 功能特性</h2>
          <div class="features-grid">
            <div class="feature-item">
              <div class="feature-icon">📝</div>
              <div class="feature-content">
                <h3>VSCode 一键打开</h3>
                <p>快速打开用户日志，提升调试效率</p>
              </div>
            </div>
            <div class="feature-item">
              <div class="feature-icon">📊</div>
              <div class="feature-content">
                <h3>日志查阅</h3>
                <p>便捷查看用户反馈日志</p>
              </div>
            </div>
            <div class="feature-item">
              <div class="feature-icon">⏰</div>
              <div class="feature-content">
                <h3>时间戳转换</h3>
                <p>实用的时间戳转换工具</p>
              </div>
            </div>
          </div>
          <div class="update-notice">
            <span class="notice-badge">持续更新</span>
            <span>更多实用功能正在开发中...</span>
          </div>
        </div>

        <div class="download-section">
          <h2 class="section-title">⬇️ 下载安装</h2>
          <div class="download-grid">
            <div v-for="(download, index) in downloads" :key="index" class="download-card">
              <div class="card-header">
                <component :is="download.icon" class="platform-icon" />
                <h3>{{ download.title }}</h3>
              </div>
              <button class="download-button" @click="open_url(download.version)">
                <download-icon class="btn-icon" />
                <span>立即下载</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <footer>
      <p> YW LOG ZAP &copy; {{ currentYear }} By Doone. 保留所有权利。</p>
    </footer>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { FulfillingBouncingCircleSpinner } from 'epic-spinners'
import { Laptop, Apple, Download as DownloadIcon } from 'lucide-vue-next'
import axios from 'axios';

const download_url_map = ref({
  'windows': '',
  'macos-x86': '',
  'macos-m1': '',
})

const currentYear = computed(() => new Date().getFullYear())
const isloading = ref(true);

const downloads = ref([
  { title: 'Windows', icon: Laptop, version: 'windows' },
  { title: 'macOS Intel芯片', icon: Apple, version: 'macos-x86' },
  { title: 'macOS M芯片', icon: Apple, version: 'macos-m1' }
])

onMounted(async () => {
  try {
    const response = await axios.get('https://api.internal.52yuwan.com/yw_log_zap/release');
    console.log(response.data);
    const { code, mac_aarch, mac_x64, win } = response.data;
    if (code === 200) {
      download_url_map.value['windows'] = win;
      download_url_map.value['macos-x86'] = mac_x64;
      download_url_map.value['macos-m1'] = mac_aarch;
      isloading.value = false;
    }
  } catch (error) {
    console.error('获取release失败:', error);
  }
});

const open_url = (version) => {
  window.open(download_url_map.value[version], '_blank');
}

// function copyToClipboard(text) {
//   navigator.clipboard.writeText(text).then(() => {
//     // 可以添加提示复制成功的逻辑
//     ElMessage({
//       message: '复制成功！',
//       type: 'success',
//       duration: 2000
//     })
//   }).catch(err => {
//     console.error('复制失败:', err);
//     ElMessage({
//       message: '复制失败，请手动复制',
//       type: 'error',
//       duration: 2000
//     })
//   });
// }
</script>

<style scoped>
/* 全局样式 */
:root {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  margin: 0;
  padding: 0;
  min-height: 100vh;
  background: #1a1a2e;
}

.download-page {
  position: relative;
  min-height: 100vh;
  background: #1a1a2e;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  overflow: hidden;
}

/* 雾状移动光效背景 */
.breathing-background {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  pointer-events: none;
}

.breathing-background::before,
.breathing-background::after {
  content: '';
  position: absolute;
  width: 600px;
  height: 600px;
  border-radius: 50%;
  filter: blur(100px);
  animation: breathe-glow 6s ease-in-out infinite;
}

.breathing-background::before {
  top: 10%;
  left: 10%;
  background: radial-gradient(circle, rgba(88, 101, 242, 0.5) 0%, rgba(88, 101, 242, 0) 70%);
  animation-delay: 0s;
}

.breathing-background::after {
  bottom: 10%;
  right: 10%;
  background: radial-gradient(circle, rgba(139, 92, 246, 0.5) 0%, rgba(139, 92, 246, 0) 70%);
  animation-delay: 3s;
}

/* 额外的呼吸灯层 */
.breathing-overlay {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 800px;
  height: 800px;
  background: radial-gradient(circle, rgba(59, 130, 246, 0.4) 0%, transparent 60%);
  filter: blur(120px);
  animation: breathe-glow 8s ease-in-out infinite;
  animation-delay: 1.5s;
  z-index: 0;
  pointer-events: none;
}

@keyframes breathe-glow {
  0%, 100% {
    opacity: 0.3;
    transform: scale(0.8);
  }
  50% {
    opacity: 1;
    transform: scale(1.3);
  }
}

/* 雪花效果 */
.snowflakes {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1;
}

.snowflake {
  position: absolute;
  top: -10%;
  color: rgba(255, 255, 255, 0.9);
  font-size: 1.5rem;
  animation: fall linear infinite;
  text-shadow: 0 0 15px rgba(200, 220, 255, 0.9);
}

.snowflake:nth-child(1) { left: 5%; animation-duration: 12s; animation-delay: 0s; font-size: 1.2rem; }
.snowflake:nth-child(2) { left: 10%; animation-duration: 15s; animation-delay: 1s; font-size: 1.8rem; }
.snowflake:nth-child(3) { left: 15%; animation-duration: 10s; animation-delay: 2s; font-size: 1rem; }
.snowflake:nth-child(4) { left: 20%; animation-duration: 13s; animation-delay: 0.5s; font-size: 1.5rem; }
.snowflake:nth-child(5) { left: 25%; animation-duration: 11s; animation-delay: 1.5s; font-size: 1.3rem; }
.snowflake:nth-child(6) { left: 30%; animation-duration: 14s; animation-delay: 0s; font-size: 1.6rem; }
.snowflake:nth-child(7) { left: 35%; animation-duration: 12s; animation-delay: 2.5s; font-size: 1.4rem; }
.snowflake:nth-child(8) { left: 40%; animation-duration: 16s; animation-delay: 1s; font-size: 1.1rem; }
.snowflake:nth-child(9) { left: 45%; animation-duration: 13s; animation-delay: 3s; font-size: 1.7rem; }
.snowflake:nth-child(10) { left: 50%; animation-duration: 11s; animation-delay: 0.5s; font-size: 1.2rem; }
.snowflake:nth-child(11) { left: 55%; animation-duration: 15s; animation-delay: 2s; font-size: 1.5rem; }
.snowflake:nth-child(12) { left: 60%; animation-duration: 12s; animation-delay: 1.5s; font-size: 1.3rem; }
.snowflake:nth-child(13) { left: 65%; animation-duration: 14s; animation-delay: 0s; font-size: 1.8rem; }
.snowflake:nth-child(14) { left: 70%; animation-duration: 10s; animation-delay: 2.5s; font-size: 1rem; }
.snowflake:nth-child(15) { left: 75%; animation-duration: 13s; animation-delay: 1s; font-size: 1.6rem; }
.snowflake:nth-child(16) { left: 80%; animation-duration: 11s; animation-delay: 0.5s; font-size: 1.4rem; }
.snowflake:nth-child(17) { left: 85%; animation-duration: 15s; animation-delay: 3s; font-size: 1.2rem; }
.snowflake:nth-child(18) { left: 90%; animation-duration: 12s; animation-delay: 2s; font-size: 1.7rem; }
.snowflake:nth-child(19) { left: 95%; animation-duration: 14s; animation-delay: 1.5s; font-size: 1.1rem; }
.snowflake:nth-child(20) { left: 3%; animation-duration: 10s; animation-delay: 0s; font-size: 1.5rem; }

@keyframes fall {
  0% {
    top: -10%;
    opacity: 1;
  }
  100% {
    top: 110%;
    opacity: 0.3;
  }
}

/* 内容包装器 */
.content-wrapper {
  position: relative;
  z-index: 2;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
  padding: 2rem 1rem;
}

/* 头部区域 */
.header {
  text-align: center;
  margin-bottom: 3rem;
}

.logo-container {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 180px;
  height: 180px;
  margin-bottom: 1.5rem;
  animation: float-logo 3s ease-in-out infinite;
}

@keyframes float-logo {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}

.logo {
  width: 100%;
  height: 100%;
  object-fit: contain;
  filter: drop-shadow(0 8px 20px rgba(139, 92, 246, 0.6));
}

.title {
  font-size: 2.5rem;
  font-weight: 800;
  color: #ffffff;
  margin: 0 0 0.5rem 0;
  letter-spacing: 2px;
  background: linear-gradient(90deg, 
    #60a5fa 0%, 
    #a78bfa 25%, 
    #f0abfc 50%, 
    #a78bfa 75%, 
    #60a5fa 100%
  );
  background-size: 200% auto;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: gradient-flow 4s linear infinite;
  filter: drop-shadow(0 0 20px rgba(139, 92, 246, 0.6));
}

@keyframes gradient-flow {
  0% {
    background-position: 0% center;
  }
  100% {
    background-position: 200% center;
  }
}

.subtitle {
  font-size: 1rem;
  margin: 0;
  font-weight: 300;
  background: linear-gradient(90deg, 
    #93c5fd 0%, 
    #c4b5fd 50%, 
    #93c5fd 100%
  );
  background-size: 200% auto;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: gradient-flow 3s linear infinite, fade-pulse 2s ease-in-out infinite;
}

@keyframes fade-pulse {
  0%, 100% {
    opacity: 0.7;
  }
  50% {
    opacity: 1;
  }
}

/* 主内容区 */
.main-content {
  display: flex;
  flex-direction: column;
  gap: 2rem;
  margin-bottom: 5rem;
}

/* 简介区域 */
.intro-section {
  background: rgba(17, 24, 39, 0.6);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  padding: 2rem;
  border: 1px solid rgba(139, 92, 246, 0.2);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
}

.section-title {
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0 0 1.5rem 0;
  text-align: left;
  background: linear-gradient(135deg, 
    #e0e7ff 0%, 
    #c7d2fe 50%, 
    #e0e7ff 100%
  );
  background-size: 200% auto;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: gradient-flow 5s linear infinite;
}

/* 功能网格 */
.features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.feature-item {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1rem;
  background: rgba(99, 102, 241, 0.05);
  border-radius: 12px;
  border: 1px solid rgba(139, 92, 246, 0.15);
  transition: all 0.3s ease;
}

.feature-item:hover {
  background: rgba(99, 102, 241, 0.1);
  border-color: rgba(139, 92, 246, 0.3);
  transform: translateX(5px);
}

.feature-icon {
  font-size: 2rem;
  flex-shrink: 0;
}

.feature-content h3 {
  font-size: 1rem;
  font-weight: 600;
  color: #c7d2fe;
  margin: 0 0 0.25rem 0;
}

.feature-content p {
  font-size: 0.875rem;
  color: #94a3b8;
  margin: 0;
  line-height: 1.5;
}

/* 更新提示 */
.update-notice {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  background: rgba(34, 197, 94, 0.1);
  border-radius: 12px;
  border: 1px solid rgba(34, 197, 94, 0.2);
  color: #86efac;
  font-size: 0.9rem;
}

.notice-badge {
  padding: 0.25rem 0.75rem;
  background: rgba(34, 197, 94, 0.2);
  border-radius: 20px;
  font-weight: 600;
  font-size: 0.75rem;
}

/* 下载区域 */
.download-section {
  background: rgba(17, 24, 39, 0.6);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  padding: 2rem;
  border: 1px solid rgba(139, 92, 246, 0.2);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
}

.download-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.download-card {
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.1), rgba(139, 92, 246, 0.1));
  border: 1px solid rgba(139, 92, 246, 0.3);
  border-radius: 16px;
  padding: 1.5rem;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.download-card:hover {
  transform: translateY(-5px);
  border-color: rgba(139, 92, 246, 0.5);
  box-shadow: 0 12px 28px rgba(99, 102, 241, 0.3);
}

.card-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.card-header h3 {
  font-size: 1rem;
  font-weight: 600;
  color: #e0e7ff;
  margin: 0;
}

.platform-icon {
  width: 28px;
  height: 28px;
  color: #a5b4fc;
}

.download-button {
  width: 100%;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  color: white;
  font-weight: 600;
  padding: 0.75rem 1.5rem;
  border-radius: 12px;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.4);
  position: relative;
  overflow: hidden;
}

.download-button::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: left 0.5s;
}

.download-button:hover::before {
  left: 100%;
}

.download-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(99, 102, 241, 0.5);
}

.btn-icon {
  width: 18px;
  height: 18px;
}

/* Mac 提示卡片 */
.mac-notice {
  background: rgba(245, 158, 11, 0.1);
  border: 1px solid rgba(245, 158, 11, 0.3);
  border-radius: 12px;
  padding: 1.25rem;
}

.notice-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}

.notice-icon {
  font-size: 1.25rem;
}

.notice-title {
  font-size: 1rem;
  font-weight: 600;
  color: #fbbf24;
}

.notice-desc {
  color: #fcd34d;
  font-size: 0.875rem;
  margin: 0 0 0.75rem 0;
  line-height: 1.5;
}

.code-block {
  position: relative;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 8px;
  padding: 0.75rem;
  border: 1px solid rgba(245, 158, 11, 0.2);
}

.code-block code {
  display: block;
  color: #a3e635;
  font-family: 'Monaco', 'Menlo', monospace;
  font-size: 0.8rem;
  word-break: break-all;
  padding-right: 60px;
}

.copy-btn {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  padding: 0.375rem 0.75rem;
  background: rgba(245, 158, 11, 0.2);
  border: 1px solid rgba(245, 158, 11, 0.3);
  border-radius: 6px;
  color: #fbbf24;
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.2s;
}

.copy-btn:hover {
  background: rgba(245, 158, 11, 0.3);
  border-color: rgba(245, 158, 11, 0.5);
}

footer {
  position: fixed;
  left: 0;
  bottom: 0;
  width: 100vw;
  background-color: rgba(45, 55, 72, 0.9);
  backdrop-filter: blur(10px);
  padding: 1rem;
  text-align: center;
  color: #a0aec0;
  z-index: 3;
  border-top: 1px solid rgba(96, 165, 250, 0.2);
}

/* 加载动画层级 */
.fullscreen-loader {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(10, 14, 39, 0.95);
  z-index: 9999;
}
</style>